import { useCallback, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { TripPlannerChat } from 'tripPlanner/api/types'
import { useChatList, useClaimChatsMutation, useDeleteChatMutation, useGenerateChatTitleMutation, useUpdateChatMutation } from 'tripPlanner/hooks/api/chat'
import { getChatLastActivityTimestamp } from 'tripPlanner/utils/chatTimestamps'

type UseChatHistoryArgs = {
  isLoggedIn: boolean
}

export function useChatHistory({ isLoggedIn }: UseChatHistoryArgs) {
  const queryClient = useQueryClient()
  const {
    data: chatHistoryResponse = [],
    isLoading: isChatListLoading,
    refetch: refetchChatList,
  } = useChatList({ enabled: isLoggedIn, staleTime: 30_000 })

  const { mutateAsync: deleteChatMutation, isLoading: isDeleteMutationLoading } = useDeleteChatMutation()
  const { mutateAsync: claimChatsMutation } = useClaimChatsMutation()
  const { mutateAsync: generateChatTitleMutation } = useGenerateChatTitleMutation()
  const { mutateAsync: updateChatMutation } = useUpdateChatMutation()

  const chatHistory: Array<TripPlannerChat> = useMemo(() => {
    if (!isLoggedIn) {
      return []
    }
    return [...chatHistoryResponse].sort((a, b) => {
      const lastActivityB = new Date(getChatLastActivityTimestamp(b)).getTime()
      const lastActivityA = new Date(getChatLastActivityTimestamp(a)).getTime()
      return lastActivityB - lastActivityA
    })
  }, [chatHistoryResponse, isLoggedIn])

  const recentChats = useMemo(() => (isLoggedIn ? chatHistory : []), [chatHistory, isLoggedIn])
  const isHistoryListLoading = isChatListLoading || isDeleteMutationLoading

  const updateChatListTitle = useCallback((chatIdToUpdate: number, title: string) => {
    queryClient.setQueryData<Array<TripPlannerChat> | undefined>(['tripChats'], (previous) => {
      if (!previous) {
        return previous
      }
      let didChange = false
      const nextChats = previous.map(chat => {
        if (chat.id !== chatIdToUpdate) {
          return chat
        }
        didChange = true
        return {
          ...chat,
          name: title,
        }
      })
      return didChange ? nextChats : previous
    })
  }, [queryClient])

  const updateChatLastActivityTimestamp = useCallback((chatIdToUpdate: number, isoTimestamp: string) => {
    queryClient.setQueryData<Array<TripPlannerChat> | undefined>(['tripChats'], (previous) => {
      if (!previous) {
        return previous
      }
      let didChange = false
      const nextChats = previous.map(chat => {
        if (chat.id !== chatIdToUpdate) {
          return chat
        }
        if (getChatLastActivityTimestamp(chat) === isoTimestamp) {
          return chat
        }
        didChange = true
        return {
          ...chat,
          updatedAt: isoTimestamp,
          lastMessageAt: isoTimestamp,
        }
      })
      return didChange ? nextChats : previous
    })
  }, [queryClient])

  const startChatTitleGeneration = useCallback(async(chatIdToUpdate: number, message: string) => {
    const trimmedMessage = message.trim()
    if (!trimmedMessage) {
      return { started: false }
    }
    try {
      const response = await generateChatTitleMutation({
        chatId: chatIdToUpdate,
        message: trimmedMessage,
      })
      updateChatListTitle(chatIdToUpdate, response.title)
      if (isLoggedIn) {
        await refetchChatList()
      }
      return { started: true }
    } catch {
      return { started: false }
    }
  }, [generateChatTitleMutation, updateChatListTitle, isLoggedIn, refetchChatList])

  return {
    chatHistory,
    recentChats,
    isHistoryListLoading,
    refetchChatList,
    deleteChatMutation,
    claimChatsMutation,
    generateChatTitleMutation,
    updateChatMutation,
    updateChatListTitle,
    updateChatLastActivityTimestamp,
    startChatTitleGeneration,
  }
}
